/**
 * cropGlyph — маппинг «имя культуры» → имя фирменного crop-глифа для BrandIcon.
 *
 * Возвращает имя файла глифа из `src/assets/brand-icons/crop-*.svg` (без
 * префикса `crop-` и без `.svg`) либо `null`, если узнаваемого соответствия
 * нет. Глифы НЕ выдумываем: нет уверенного совпадения → `null` → в UI просто
 * не показываем иконку (чип рендерится как раньше, без пустого места).
 *
 * Матчинг — по вхождению ключевого слова (стемма) в нормализованное имя:
 * lower-case, ё→е, схлопнутые пробелы, trim. Порядок правил важен —
 * более специфичные семейства (пшеница/рожь/виноград/бахчевые) разбираются
 * до простого списка.
 *
 * Дефолты по подтипу (не «выдумывание» глифа, а разумный агрономический
 * дефолт когда подтип в названии не указан):
 *   • пшеница без «озим/яров» → озимая (доминирует в РФ);
 *   • пшеница без «тверд/дурум» → мягкая;
 *   • рожь без «яров» → озимая;
 *   • виноград без «техн» → столовый (grapes-table).
 *
 * Доступные crop-глифы (23): sunflower, corn, rye-spring, rye-winter,
 * wheat-{winter,spring}-{soft,hard}, cherry, blueberry, pea, mustard,
 * buckwheat, vicia, broccoli, apricot, eggplant, watermelon, melon, melons,
 * pumpkin, grapes-technical, grapes-table.
 */

/** Нормализация имени культуры: lower, ё→е, схлопнуть пробелы, trim. */
function normalizeCropName(name: string): string {
  return name
    .toLowerCase()
    .replace(/ё/g, 'е')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Простые (одноглифовые) культуры: список стемм → имя глифа. Проверяется
 * после семейств со специальным разбором подтипа.
 */
const SIMPLE_GLYPHS: ReadonlyArray<readonly [readonly string[], string]> = [
  [['кукуруз'], 'corn'],
  [['подсолн'], 'sunflower'],
  [['гречих', 'гречк'], 'buckwheat'],
  [['горчиц'], 'mustard'],
  [['горох', 'горош'], 'pea'],
  [['вика', 'вико', 'вики', 'вику'], 'vicia'],
  [['брокколи'], 'broccoli'],
  [['вишн'], 'cherry'],
  [['голубик'], 'blueberry'],
  [['абрикос'], 'apricot'],
  [['баклажан'], 'eggplant'],
  [['арбуз'], 'watermelon'],
  [['дын'], 'melon'],
  [['тыкв'], 'pumpkin'],
];

/**
 * По имени культуры возвращает имя crop-глифа (для `<BrandIcon name={`crop-${g}`}/>`)
 * или `null`, если узнаваемого соответствия нет.
 */
export function cropGlyph(name: string | null | undefined): string | null {
  if (!name) return null;
  const s = normalizeCropName(name);
  if (!s) return null;

  const has = (...keys: string[]): boolean => keys.some((k) => s.includes(k));

  // Пшеница — озимая/яровая × мягкая/твёрдая.
  if (has('пшениц')) {
    const season = has('яров') && !has('озим') ? 'spring' : 'winter';
    const kind = has('тверд', 'дурум') ? 'hard' : 'soft';
    return `wheat-${season}-${kind}`;
  }

  // Рожь — озимая/яровая.
  if (has('рожь', 'ржи', 'ржан')) {
    return has('яров') ? 'rye-spring' : 'rye-winter';
  }

  // Виноград — технический/столовый.
  if (has('виноград')) {
    return has('техн') ? 'grapes-technical' : 'grapes-table';
  }

  // Бахчевые (сборное) — раньше дыни/арбуза/тыквы.
  if (has('бахчев')) return 'melons';

  for (const [keys, glyph] of SIMPLE_GLYPHS) {
    if (has(...keys)) return glyph;
  }

  return null;
}
